<template>
  <ModalWithPages
    v-bind="{ ...$props, ...$attrs }"
    persistent
    :loading="loading"
    @next-page="$emit('nextPage')"
    @prev-page="$emit('prevPage')"
    @close="$emit('close')"
  >
    <template #title>
      Job Post Revisions
    </template>
    <template #paged>
      <div class="diff-modal-revision-side">
        <DiffRevision
          key="historical"
          :revision="formattedHistoricalRevision"
          :start-score-animation="startScoreAnimation"
          :created-data="selectedHistoricalRevisionCreatedData"
          :disable-buttons="disabled"
          :loading="loading"
          :show-text="{ showLocation, showReqId }"
          @revert-to-revision="revertToRevision"
        />
      </div>
    </template>
    <template #static>
      <div class="diff-modal-revision-side current">
        <DiffRevision
          key="current"
          :revision="formattedCurrentRevision"
          :start-score-animation="startScoreAnimation"
          :created-data="currentRevisionCreatedData"
          :disable-buttons="disabled"
          :loading="loading"
          :show-text="{ showLocation, showReqId }"
          @revert-to-revision="revertToRevision"
        />
      </div>
    </template>
  </ModalWithPages>
</template>

<script>
import ModalWithPages from '@/components/shared/ModalWithPages.vue';
import DiffRevision from '@/components/editor/DiffRevision.vue';
import { mapActions, mapState } from 'pinia';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useEditorDiffModal } from '@/stores/editorDiffModal/editorDiffModal';
import { getHTMLDiff } from '@/helpers/diff';
import { recruitRevert } from '@/api/recruit';
import { recruitJsonAjaxAnalyze } from '@/api/recruitJson';
import { joinFooterAndHeaderFields } from '@/helpers/headerFooterFields';

const DEFAULT_CREATED_DATA = {
  createdBy: '',
  created: '',
};

const DEFAULT_DIFF = {
  current: '',
  previous: '',
};

export default {
  name: 'DiffModal',
  components: {
    ModalWithPages,
    DiffRevision,
  },
  props: {
    // Passes through as the value to show the modal
    modelValue: { type: Boolean, default: false },
    jobId: { type: [String, Number], default: '' },
    disabled: { type: Boolean, default: false },
  },
  emits: ['nextPage', 'prevPage', 'close'],
  data() {
    return {
      formattedHistoricalRevision: {},
      formattedCurrentRevision: {},
      startScoreAnimation: false,
    };
  },
  computed: {
    ...mapState(useEditorDiffModal, [
      'loading',
      'selectedHistoricalRevision',
      'prevHistoricalRevision',
      'nextHistoricalRevision',
      'currentRevision',
      'page',
      'fetchedRevisions',
    ]),
    showLocation() {
      const { currentRevision, selectedHistoricalRevision } = this;

      return currentRevision?.location || selectedHistoricalRevision?.location;
    },
    showReqId() {
      const { currentRevision, selectedHistoricalRevision } = this;

      return currentRevision?.ats_requisition_id || selectedHistoricalRevision?.ats_requisition_id;
    },
    currentRevisionCreatedData() {
      const currentRevisionId = this.currentRevision?.id;

      if (currentRevisionId) {
        const allRevisions = this.currentRevision.allRevisions || [];
        const currentRevision = allRevisions.filter(({ id }) => id === currentRevisionId)[0];

        return currentRevision || DEFAULT_CREATED_DATA;
      }

      return DEFAULT_CREATED_DATA;
    },
    selectedHistoricalRevisionCreatedData() {
      const selectedHistoricalRevisionId = this.selectedHistoricalRevision?.id;

      if (selectedHistoricalRevisionId) {
        const allRevisions = this.selectedHistoricalRevision.allRevisions || [];
        const selectedHistoricalRevision = allRevisions
          .filter(({ id }) => id === selectedHistoricalRevisionId)[0];

        return selectedHistoricalRevision || DEFAULT_CREATED_DATA;
      }

      return DEFAULT_CREATED_DATA;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        if (newValue && this.jobId) {
          setTimeout(() => {
            this.startScoreAnimation = true;
          }, 500);
        } else {
          this.startScoreAnimation = false;
        }
      },
    },
    currentRevision(newValue) {
      if (newValue && this.selectedHistoricalRevision) {
        this.updateRevisions(newValue, this.selectedHistoricalRevision);
      }
    },
    selectedHistoricalRevision(newValue) {
      if (newValue && this.currentRevision) {
        this.updateRevisions(this.currentRevision, newValue);
      }
    },
  },
  methods: {
    ...mapActions(useAppSnackbar, ['addErrorSnackbar']),
    async revertToRevision() {
      try {
        await recruitRevert({
          jobId: this.jobId,
          revisionId: this.selectedHistoricalRevision.id,
        });

        const { jobId: id } = await recruitJsonAjaxAnalyze({
          jobId: this.jobId,
          revisionId: this.selectedHistoricalRevision.id,
        });

        const searchParams = new URLSearchParams();
        const { key, view } = this.$route.query;

        if (view) {
          searchParams.append('view', view);
        }

        if (key) {
          searchParams.append('key', key);
        }

        this.$emit('close');
        window.location.replace(`/app/view/reload/${id}?${searchParams}`);
      } catch (err) {
        if (err.response?.code === 'document_not_found') {
          this.addErrorSnackbar({ text: 'Revert to revision error: Document not found' });
          return;
        }

        this.addErrorSnackbar({ text: `Revert to revision error: ${err.message}` });
        throw err;
      }
    },
    aggregateFieldHTML(field) {
      let html = '';

      field.sections.forEach((section) => {
        section.blocks.forEach((block) => {
          html += block.html;
        });
      });

      return html;
    },
    updateRevisions(currentRevision, selectedHistoricalRevision) {
      const formattedCurrent = this.formatRevision(currentRevision);
      const formattedHistorical = this.formatRevision(selectedHistoricalRevision);

      const { current, previous } = this.diffRevisions(formattedCurrent, formattedHistorical);

      this.formattedCurrentRevision = current;
      this.formattedHistoricalRevision = previous;
    },
    formatRevision(revision) {
      const revisionFields = joinFooterAndHeaderFields(revision.description_json?.fields || []);
      const newFields = revisionFields.map((field) => ({
        label: field.label,
        html: this.aggregateFieldHTML(field),
        id: field.id,
        type: field.type?.toLowerCase(),
      }));

      return {
        ...revision,
        fields: newFields,
      };
    },
    diffRevisions(current, previous) {
      const newCurrent = { ...current };
      const newPrevious = { ...previous };
      const fieldDiffs = current.fields.map((field) => {
        const historicalHtml = previous.fields.filter((prevField) => prevField.type === field.type)[0]?.html || '';
        const diff = typeof historicalHtml === 'string' ? getHTMLDiff(field.html, historicalHtml) : field.html;
        return { diff, type: field.type };
      });

      fieldDiffs.forEach(({ diff, type }) => {
        const newCurrentFields = newCurrent.fields.map((field) => {
          const el = document.createElement('div');
          el.innerHTML = diff.current;
          const hasText = Boolean(el.textContent);

          if (type === field.type) {
            return {
              ...field,
              diffedHTML: diff.current,
              hasText,
            };
          }

          return field;
        });

        newCurrent.fields = newCurrentFields;

        const newPreviousFields = newPrevious.fields.map((field) => {
          if (type === field.type) {
            const el = document.createElement('div');
            el.innerHTML = diff.previous;
            const hasText = Boolean(el.textContent);

            return {
              ...field,
              diffedHTML: diff.previous,
              hasText,
            };
          }

          return field;
        });

        newPrevious.fields = newPreviousFields;
      });

      const currentTitle = current.title || '';
      const previousTitle = previous.title || '';
      const diffedTitle = getHTMLDiff(currentTitle, previousTitle) || DEFAULT_DIFF;

      newCurrent.diffedTitle = diffedTitle.current;
      newPrevious.diffedTitle = diffedTitle.previous;

      const currentCompany = current.company || '';
      const previousCompany = previous.company || '';
      const diffedCompany = getHTMLDiff(currentCompany, previousCompany) || DEFAULT_DIFF;

      newCurrent.diffedCompany = diffedCompany.current;
      newPrevious.diffedCompany = diffedCompany.previous;

      const currentLocation = current.location || '';
      const previousLocation = previous.location || '';
      const diffedLocation = getHTMLDiff(currentLocation, previousLocation) || DEFAULT_DIFF;

      newCurrent.diffedLocation = diffedLocation.current;
      newPrevious.diffedLocation = diffedLocation.previous;

      const currentReqId = current.ats_requisition_id || '';
      const previousReqId = previous.ats_requisition_id || '';
      const diffedReqId = getHTMLDiff(currentReqId, previousReqId) || DEFAULT_DIFF;

      newCurrent.diffedReqId = diffedReqId.current;
      newPrevious.diffedReqId = diffedReqId.previous;

      return { current: newCurrent, previous: newPrevious };
    },
  },
};
</script>

<style lang="less" scoped>
.diff-modal-revision-side {
  overflow-x: hidden;
  background: white;
  &:last-of-type {
    border-left: 1px solid @stroke-default;
  }
  &.current {
    flex: 1 1 0px;
    z-index: 2;
  }
}
.revision-side-loader {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
